import {FC, ReactNode} from "react";
import {css, cx} from "@pandacss/css";
import {font} from "../style/typography.tsx";

type ButtonSize = "small" | "medium" | "large" | "circle"
type ButtonVariant = "primary" | "outline" | "light";


interface Props {
    children: ReactNode;
    size?: ButtonSize;
    variant?: ButtonVariant;
    onClick?: () => void;
    disabled?: boolean;
    pressed?: boolean;
}

const baseClass = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px"
})

const sizes: Record<ButtonSize, string> = {
    circle: css({
        height: '44px',
        padding: '0 10px',
        borderRadius: '22px'
    }),
    small: css({
        height: "28px",
        padding: "0 7px",
        borderRadius: "14px",
    }),
    medium: css({
        height: "36px",
        padding: "0 11px",
        borderRadius: "18px",
    }),
    large: css({
        height: "48px",
        padding: "0 15px",
        borderRadius: "24px",
    })
}

const variants: Record<ButtonVariant, string> = {
    primary: css({
        background: "primary.500",
        color: "neutral.100",
        boxShadow: "0px 2px 1px 1px rgba(255, 255, 255, 0.25) inset, 0px -1px 1px 1px rgba(0, 0, 0, 0.25) inset",
        borderLeft: "1px solid transparent",
        borderRight: "1px solid transparent",

        "&:disabled": {
            background: "neutral.350",
            color: "neutral.250",
        },

        "&.pressed,&:active": {
            background: "primary.600",
        }
    }),
    outline: css({
        background: "neutral.700",
        border: "1px solid",
        borderColor: "neutral.500",
        color: "neutral.100",

        "&:disabled": {
            background: "neutral.350",
            border: "1px solid",
            borderColor: "neutral.500",
            color: "neutral.250",
        },

        "&.pressed,&:active": {
            background: "neutral.900",
            border: "1px solid",
            borderColor: "neutral.500",
        }
    }),
    light: css({
        background: "primary.300",
        color: "primary.700",
        boxShadow: "0px 2px 1px 1px rgba(255, 255, 255, 0.25) inset, 0px -2px 1px 1px rgba(0, 0, 0, 0.25) inset",
        borderLeft: "1px solid transparent",
        borderRight: "1px solid transparent",

        "&:disabled": {
            background: "neutral.350",
            color: "neutral.250",
        },

        "&.pressed,&:active": {
            background: "primary.400",
            color: "primary.800",
        }
    }),
}

const Button: FC<Props> = ({children, size = "medium", variant = "primary", onClick, disabled, pressed}) => {
    const buttonClass = cx(baseClass, sizes[size], variants[variant], pressed && "pressed", font("body", "3", "semiBold"))

    return <button className={buttonClass} onClick={onClick} disabled={disabled}>{children}</button>
}

export default Button;